/**
 * Kinde configuration.
 *
 * Every one of these values was verified against a live token and the Kinde dashboard on
 * 2026-08-16, not inferred. Where a value looks surprising, the surprise is documented — most
 * of them cost a round trip to discover and would fail at runtime with an opaque 401.
 *
 * We REUSE the existing application rather than creating a second one. Kinde users belong to
 * the business, not the application, so a new application would also have worked for shared
 * accounts — but reuse means one callback list and one connection set to keep correct, and
 * this application is already a public client with no secret, which was the only real risk.
 *
 * Sign-in uses **Kinde's hosted screens**, as the current product does through this same
 * application. The "Use your own sign-up and sign-in screens" setting on it permits custom
 * screens; it does not require them. See `features/auth/SignInRoute.tsx` for why hosted won.
 */

/**
 * The custom domain, NOT `awarenow.kinde.com`.
 *
 * Both hostnames are live and each declares ITSELF as `issuer` in its own OIDC discovery
 * document, so this is a 50/50 guess with a total auth outage on the wrong side. A live token
 * carries `iss: https://auth.awarenow.ai`, and the API validates `iss` strictly.
 *
 * `auth.awarenow.ai` is a CNAME to `us.kinde.com` — Kinde's custom domain feature. It is not
 * our server, despite the name sitting on our apex.
 *
 * Being on a custom domain also matters for security, not just naming: the Kinde SDK can then
 * hold refresh tokens in a first-party cookie instead of localStorage, which is why
 * `useInsecureForRefreshToken` is never set.
 */
export const KINDE_DOMAIN = 'https://auth.awarenow.ai';

/**
 * "My React App" — the application that actually issues browser tokens, confirmed by the
 * `azp` claim on a live token.
 *
 * NOT `a9b1751fcc294dbea016697a07d9edaf`. That id appears in the Django backend's source and
 * is easy to mistake for this one, but it belongs to a separate application named "backend",
 * registered as **Back-end web** — a confidential client that a browser could not use at all.
 */
export const KINDE_CLIENT_ID = 'a5b61d0049434ea1bea1b3ddc26b028a';

/**
 * The audience this app requests, which becomes the `aud` claim the API verifies.
 *
 * **Requesting it is mandatory, not optional.** A live token from this application currently
 * carries `aud: []` — present but empty — because the tenant has no custom API defined. The
 * Insights API verifies `aud` strictly and rejects anything that does not match, so with an
 * empty claim every request 401s.
 *
 * Three things must all be true, and two of them are outside this repo:
 *   1. an API exists in Kinde with this exact audience
 *   2. this application is authorized to it
 *   3. the client asks for it — that is this constant
 *
 * Doing only 1 and 2 leaves `aud` empty. Kinde stamps the claim only when asked.
 */
export const KINDE_AUDIENCE = 'https://aware-insights-api';

/**
 * `offline`, NOT `offline_access`.
 *
 * This tenant's discovery document lists `scopes_supported` as
 * `address, email, offline, openid, phone, profile`. `offline_access` — the name used in most
 * OIDC documentation and in Kinde's own examples — is not among them, and requesting it fails.
 * The live token's `scp` claim is `["email","profile","openid","offline"]`.
 */
export const KINDE_SCOPE = 'openid profile email offline';

/**
 * Where Kinde returns the browser after authenticating.
 *
 * Derived from `window.location.origin` so local dev, the Static Web Apps preview
 * environments, and production all work without a build-time variable. Every origin still has
 * to be added to the application's allowed callback list in Kinde — a redirect URI that is not
 * registered fails with a generic error that does not say which URL was rejected.
 *
 * The shared application already carries 21 callback URLs across four domains. Adding to that
 * list is the cost of reuse.
 */
export const redirectUri = (): string => `${window.location.origin}/callback`;
export const logoutUri = (): string => window.location.origin;

/**
 * Sign-in methods are NOT configured here.
 *
 * Kinde presents them on its own hosted screen, so enabling a seventh provider is a dashboard
 * change with no deploy. That is the main reason we do not keep a list in code: a hand-built
 * list that falls out of date does not look broken, it looks like the missing provider was
 * never offered — and the person who signed up with it simply cannot get in.
 *
 * For the record, verified 2026-08-16: passwordless email code, plus Apple, Google, LinkedIn,
 * Microsoft, Slack and X. Every password connection is disabled. The connection ids, should a
 * provider-specific deep link ever be wanted, are in ARCHITECTURE.md.
 */

/**
 * The billing provider's CLIENT-SIDE token.
 *
 * Public by design — it is readable in any bundle and identifies the account, not the caller.
 * It authorises opening a checkout for a transaction that already exists; it cannot create,
 * price, or modify one. That is why it can live here while the API key never leaves Key Vault.
 *
 * Supplied at build time so it can differ between environments. Empty means checkout is not
 * configured, which `CheckoutRoute` reports rather than failing silently.
 */
export const PADDLE_CLIENT_TOKEN: string = import.meta.env.VITE_PADDLE_CLIENT_TOKEN ?? '';
